import { useAuth } from "@assetwatch/auth";
import { Headline, Label, Text } from "@assetwatch/ui";
import { useTranslation } from "react-i18next";

type AuthUser = ReturnType<typeof useAuth>["user"];

export function OverviewHeader() {
  const { user } = useAuth();
  const { t, i18n } = useTranslation();

  type DayPeriod = "morning" | "afternoon" | "evening";

  const greetingKeyByPeriod: Record<DayPeriod, string> = {
    morning: "enterprise.overview.pageSubtitleMorning",
    afternoon: "enterprise.overview.pageSubtitleAfternoon",
    evening: "enterprise.overview.pageSubtitleEvening",
  };

  function getLocalTimeZone() {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  }

  function getDayPeriodForDate(date: Date, timeZone?: string): DayPeriod {
    const hour = Number(
      new Intl.DateTimeFormat("en-US", {
        hour: "numeric",
        hour12: false,
        timeZone,
      }).format(date),
    );

    if (hour < 12) {
      return "morning";
    }

    if (hour < 18) {
      return "afternoon";
    }

    return "evening";
  }

  function getOverviewGreetingName(user: AuthUser | null | undefined) {
    const firstName = user?.firstName?.trim();
    if (firstName) {
      return firstName;
    }

    const name = user?.name?.trim().split(/\s+/)[0];
    if (name) {
      return name;
    }

    const emailName = user?.email?.split("@")[0]?.trim();
    return emailName || undefined;
  }

  const locale = i18n.resolvedLanguage ?? i18n.language;
  const timeZone = getLocalTimeZone();
  const now = new Date();
  const currentDate = new Intl.DateTimeFormat(locale, {
    weekday: "long",
    month: "long",
    day: "numeric",
    year: "numeric",
    timeZone,
  }).format(now);
  const greetingName = getOverviewGreetingName(user);
  const greeting = t(greetingKeyByPeriod[getDayPeriodForDate(now, timeZone)], {
    name: greetingName,
  });

  return (
    <div className="flex min-w-0 flex-col justify-center gap-(--spacing-8)">
      <Headline size="sm" as="h1">
        {t("enterprise.overview.pageTitle")}
      </Headline>
      <Text as="div" size="md" tone="subtle">
        {greeting}
        {" | "}
        <Label size="lg" tone="subtle">
          {currentDate}
        </Label>
      </Text>
    </div>
  );
}
